import React from "react";
import { Switch, Route, Redirect, useParams } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./hooks/use-auth";
import { LanguageProvider } from "./hooks/use-language";
import { ThemeProvider } from "./lib/theme-context";
import { HelmetProvider } from "react-helmet-async";
import { ProtectedRoute } from "./lib/protected-route";
import { Toaster } from "./components/ui/toaster";
import { SessionRecoveryAlert } from "./components/session-recovery-alert";
import { InactivityProvider } from "./components/providers/inactivity-provider";
import ErrorBoundary from "./components/error-boundary";

// Pages
import SimpleLogin from "./pages/simple-login";
import Landing from "./pages/landing";
import RegisterPage from "./pages/register-page";
import VerifyEmailPage from "./pages/verify-email-page";
import PricingPage from "./pages/pricing";
import Dashboard from "./pages/dashboard";
import ClientsPage from "./pages/clients-page-new";
import ClientDetailPage from "./pages/client-detail-page";
import EstimatesPage from "./pages/estimates-page";
import InvoicesPage from "./pages/invoices-page";
import ProjectsPage from "./pages/projects-page";
import EventsPage from "./pages/events-page";
import SuperAdminDashboard from "./pages/super-admin-dashboard";
import CalendarPage from "./pages/calendar-page";
import TimeClockPage from "./pages/timeclock-page";
import AIRoofMeasurementPage from "./pages/ai-roof-measurement-page";
import SiteVisitPage from "./pages/site-visit-page";
import SettingsPage from "./pages/settings-page";
import BillingPage from "./pages/billing";
import SimplePricingPage from "./pages/simple-pricing-page";
import EstimateCreateServicePage from "./pages/estimate-create-service-page";
import EstimateCreatePage from "./pages/estimate-create-page";
import PublicEstimateView from "./pages/public-estimate-view";
import PublicInvoiceView from "./pages/public-invoice-view";
import InvoiceDetailPage from "./pages/invoice-detail-page";
import EstimatePrintPage from "./pages/estimate-print-page";
import MultiServiceEstimatePage from "./pages/multi-service-estimate-page";
import ProfessionalEstimatePage from "./pages/professional-estimate-page";
import PremiumEstimatePage from "./pages/premium-estimate-page";
import EstimateEditPage from "./pages/estimate-edit-page";
import AgentManagementPage from "./pages/agent-management-page";
import AgentEstimateFormPage from "./pages/agent-estimate-form-page";
import ClientPortal from "./pages/client-portal";
import LeadCapturePage from "./pages/lead-capture-page";
import ProjectCreatePage from "./pages/project-create-page";
import ProjectDetailPage from "./pages/project-detail-page";
import ProjectEditPage from "./pages/project-edit-page";
import PasswordResetRequest from "./pages/password-reset-request";
import PasswordResetComplete from "./pages/password-reset-complete";
import TermsPage from "./pages/terms-page";
import PrivacyPage from "./pages/privacy-page";
import AcceptInvitePage from "./pages/accept-invite-page";
import ScheduleDemoPage from "./pages/schedule-demo-page";
import RoofingSoftwarePage from "./pages/roofing-software-page";
import ContractorEstimatingSoftwarePage from "./pages/contractor-estimating-software-page";

function EstimateIdRedirectToEdit() {
  const { id } = useParams<{ id: string }>();
  return id ? <Redirect to={`/estimates/edit/${id}`} /> : <Redirect to="/estimates" />;
}

function Router() {
  return (
    <Switch>
      <Route path="/" component={Landing} />
      <Route path="/test-dashboard" component={Dashboard} />
      <Route path="/login" component={SimpleLogin} />
      <Route path="/register" component={RegisterPage} />
      <Route path="/verify-email" component={VerifyEmailPage} />
      <Route path="/simple-login" component={SimpleLogin} />
      <Route path="/landing" component={Landing} />
      <Route path="/pricing" component={PricingPage} />
      <Route path="/roofing-software" component={RoofingSoftwarePage} />
      <Route path="/contractor-estimating-software" component={ContractorEstimatingSoftwarePage} />
      <ProtectedRoute path="/dashboard" component={Dashboard} />
      <ProtectedRoute path="/clients" component={ClientsPage} requiredFeature="clients" />
      <ProtectedRoute path="/clients/:id" component={ClientDetailPage} requiredFeature="clients" />
      <ProtectedRoute path="/estimates" component={EstimatesPage} requiredFeature="estimates" />
      <ProtectedRoute path="/invoices" component={InvoicesPage} requiredFeature="invoices" />
      <ProtectedRoute path="/projects" component={ProjectsPage} requiredFeature="projects" />
      <Route path="/materials"><Redirect to="/dashboard" /></Route>
      <Route path="/ai-assistant"><Redirect to="/dashboard" /></Route>
      <ProtectedRoute path="/events" component={EventsPage} requiredFeature="calendar" />
      <ProtectedRoute path="/super-admin" component={SuperAdminDashboard} />
      <ProtectedRoute path="/calendar" component={CalendarPage} requiredFeature="calendar" />
      <ProtectedRoute path="/timeclock" component={TimeClockPage} requiredFeature="timeclock" />
      <Route path="/tools"><Redirect to="/dashboard" /></Route>
      <Route path="/tools/measurements"><Redirect to="/dashboard" /></Route>
      <Route path="/tools/ai-roof-measurement"><Redirect to="/ai-roof-measurement" /></Route>
      <ProtectedRoute path="/ai-roof-measurement" component={AIRoofMeasurementPage} />
      <ProtectedRoute path="/site-visit" component={SiteVisitPage} />
      <ProtectedRoute path="/settings" component={SettingsPage} requiredFeature="settings" />
      <ProtectedRoute path="/settings/team" component={SettingsPage} requiredFeature="settings" />
      <ProtectedRoute path="/settings/role-setup" component={SettingsPage} requiredFeature="settings" />
      <ProtectedRoute path="/billing" component={BillingPage} />
      <ProtectedRoute path="/simple-pricing" component={SimplePricingPage} requiredFeature="services" />
      {/* LOCKED: /estimates/create must stay AgentEstimateFormPage. See docs/ESTIMATE_FORM_LOCK.md */}
      <ProtectedRoute path="/estimates/create" component={AgentEstimateFormPage} requiredFeature="estimates" />
      <ProtectedRoute path="/estimates/professional" component={ProfessionalEstimatePage} requiredFeature="estimates" />
      <Route path="/estimates/:id/print" component={EstimatePrintPage} />
      <ProtectedRoute path="/estimates/multi-service" component={MultiServiceEstimatePage} requiredFeature="estimates" />
      <ProtectedRoute path="/estimates/premium" component={PremiumEstimatePage} requiredFeature="estimates" />
      <ProtectedRoute path="/estimates/premium/:id" component={PremiumEstimatePage} requiredFeature="estimates" />
      <ProtectedRoute path="/estimates/edit/:id" component={EstimateEditPage} requiredFeature="estimates" />
      <ProtectedRoute path="/estimates/:id" component={EstimateIdRedirectToEdit} requiredFeature="estimates" />
      <Route path="/public/estimates/:id" component={PublicEstimateView} />
      <Route path="/public/invoices/:id" component={PublicInvoiceView} />
      <ProtectedRoute path="/invoices/:id" component={InvoiceDetailPage} requiredFeature="invoices" />
      <ProtectedRoute path="/agents" component={AgentManagementPage} requiredFeature="agents" />
      {/* LOCKED: Agent estimate form route. Do not remove. See docs/ESTIMATE_FORM_LOCK.md */}
      <ProtectedRoute path="/agents/estimate-form" component={AgentEstimateFormPage} requiredFeature="estimates" />
      <ProtectedRoute path="/projects/create" component={ProjectCreatePage} requiredFeature="projects" />
      <ProtectedRoute path="/projects/:id" component={ProjectDetailPage} requiredFeature="projects" />
      <ProtectedRoute path="/projects/:id/edit" component={ProjectEditPage} requiredFeature="projects" />
      <Route path="/client-portal/:clientId" component={ClientPortal} />
      <Route path="/lead-capture" component={LeadCapturePage} />
      <Route path="/password-reset-request" component={PasswordResetRequest} />
      <Route path="/reset-password" component={PasswordResetComplete} />
      <Route path="/accept-invite" component={AcceptInvitePage} />
      <Route path="/terms" component={TermsPage} />
      <Route path="/privacy" component={PrivacyPage} />
      <Route path="/schedule-demo" component={ScheduleDemoPage} />
    </Switch>
  );
}

function App() {
  console.log('🚀 App component rendering - FORCE DEPLOYMENT');
  
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider>
            <HelmetProvider>
              <LanguageProvider>
                <InactivityProvider>
                  <Router />
                  <SessionRecoveryAlert />
                  <Toaster />
                </InactivityProvider>
              </LanguageProvider>
            </HelmetProvider>
          </ThemeProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;

// FORCE FRESH DEPLOYMENT - VERCEL FUNCTION LIMIT FIXED
